import { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Button, Checkbox,
  fade,
  ListItemText,
  makeStyles,
  MenuItem,
  Select,
  TextField,
} from '@material-ui/core';
import cx from 'classnames';
import { alpha } from '@material-ui/core/styles';
import { useSelector } from 'react-redux';
import { camelCase } from 'lodash/string';
import './index.css';
import { Badge } from '../../../Badge';
import getStatusBadge, { getTagsColor } from '../../../../../helpers/getStatusBadge';

const useStyles = makeStyles((theme) => ({
  inputRoot: {
    width: '100%',
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
    height: '100%',

    '& .MuiSelect-icon': {
      top: 'initial',
    },
  },
  input: {
    fontSize: 14,
    fontWeight: 400,
    whiteSpace: 'nowrap',

    '&:focus': {
      backgroundColor: 'transparent !important',
    },
  },
  buttonContainer: {
    cursor: 'default',
    position: 'sticky',
    bottom: 0,
    right: 0,
    left: 0,
    backgroundColor: 'white',
    zIndex: 2,
    paddingBottom: 12,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',

    '&:hover': {
      backgroundColor: 'white',
    },
  },
  childItem: {
    paddingLeft: 46,
    color: theme.colors.grey2,
  },
  selected: {
    backgroundColor: 'rgba(0, 0, 0, 0.15)',

    '&:hover': {
      backgroundColor: 'rgba(0, 0, 0, 0.2)',
    },
  },
  parentItemText: {
    '& span': {
      fontWeight: 500,
    },
  },
  bottomDrawer: {
    borderBottom: `1px solid ${fade('#000000', 0.1)}`,
  },
  withBubble: {
    gap: 10,
    padding: '3px 24px 3px 10px',
  },
  childItemText: {
    color: alpha(theme.colors.grey2, .8),
  },
  button: {
    width: '100%',
    marginTop: 6,
  },
  paper: {
    maxHeight: 500,
    width: 'fit-content',
    minWidth: 'fit-content !important',

    '& .MuiMenu-list': {
      paddingTop: 0,
    },
  },
  select: {
    maxHeight: 300,
  },
  searchInput: {
    width: '100%',
  },
  searchContainerMenuItem: {
    padding: 20,
    paddingTop: 12,
    position: 'sticky',
    top: 0,
    backgroundColor: 'white !important',
    zIndex: 2,
  },
  badges: {
    minWidth: 'fit-content',
    maxWidth: 'fit-content',
    fontSize: 10,
    fontWeight: 400,
    padding: 8,
    textTransform: 'none',
  },
}));

const SelectFilter = ({
                        filterValue,
                        dataKey,
                        columnData: { isFilterable, options = [], withBubble },
                        onChange,
                        containerElement,
                      }) => {
  const classes = useStyles();
  const inputElement = useRef();
  const [value, setValue] = useState([]);
  const [selectorOptions, setSelectorOptions] = useState([]);
  const [open, setOpen] = useState(false);
  const { en: { tables, btn } } = useSelector((state) => state.i18n);

  const updateOptions = () => {
    if (filterValue === '' || (Array.isArray(filterValue) && filterValue.length === 0)) {
      setValue(['']);
    } else {
      setValue(filterValue || []);
    }
    setSelectorOptions([
      { label: tables.all, value: '' },
      ...options,
    ]);
  };

  useEffect(() => {
    updateOptions();
  }, []);

  useEffect(() => {
    updateOptions();
  }, [options]);

  useEffect(() => {
    if (filterValue === '' || (Array.isArray(filterValue) && filterValue.length === 0)) {
      setValue(['']);
    } else {
      setValue(
        Array.isArray(filterValue)
          ? filterValue
          : (filterValue
          ? filterValue.split(',')
          : ''),
      );
    }
  }, [filterValue]);

  const handleSelectClose = () => {
    onChange && onChange(dataKey, value);
    handleClose();
  };

  const handleSelectChange = (event, data) => {
    const {
      target: { value: newValue },
    } = event;

    if (event.currentTarget.id === 'search') {
      return;
    }

    if (newValue.includes('button')) {
      return;
    }

    if (newValue.length === 0
      || newValue[newValue.length - 1] === ''
      || newValue.filter(filterValue => filterValue !== '').length === options.length) {
      setValue(['']);
      return;
    }

    const isItParentItem = data.props.value.includes('parent_item');

    if (!data.props.value) {
      setValue(selectorOptions.map((i) => i.value));
    }

    if (isItParentItem) {
      const newValue = data.props.value.replace('parent_item', '').split('@@@');
      const isSelected = newValue.every(r => value.includes(r));
      let newValues = [];
      let prevValues = [...value].filter((i) => !!i);

      if (isSelected) {
        newValues = [...prevValues.filter(filterValue => !newValue.includes(filterValue))];
      } else {
        newValues = [...prevValues, ...newValue];
      }

      setValue(newValues.length === 0
        || newValues.length === selectorOptions.filter((i) => !i.child && !!i.value).length
        ? ['']
        : newValues,
      );
    } else {
      let diff;
      if (newValue.length > value.length) {
        diff = newValue.filter((x) => !value.includes(x));
      } else {
        diff = value.filter((x) => !newValue.includes(x));
      }

      if (diff[0] === '') {
        setValue(['']);
      } else {
        let result = [];
        let removeItems = [];
        let _newValue = [...(typeof newValue === 'string' ? newValue.split(',') : newValue)];
        const parentItems = selectorOptions.filter(item => !!item.child);
        const parentItemsValues = parentItems.map(i => i.value);

        _newValue.forEach(item => {
          if (parentItemsValues.includes(item)) {
            const child = parentItems.find(parent => parent.value === item)?.child ?? [];
            const isSelected = child.every(r => newValue.includes(r));
            if (isSelected) {
              result = [...result, ...child];
            } else {
              result = [...result, ...child.filter(r => !newValue.includes(r))];
              removeItems = [...removeItems, ...child.filter(r => newValue.includes(r))];
            }
          } else {
            if (item !== '') {
              result = [...result, item];
            }
          }
        });
        setValue(result.filter(i => !removeItems.includes(i)));
      }
    }
  };

  const getLabelByKey = (selected) => {
    let renderedValue = '';

    selected.forEach((val) => {
      const option = selectorOptions.find((key) => key.value === val);
      const label = option ? option.label : '';
      renderedValue += `${label}, `;
    });

    return renderedValue.slice(0, -2);
  };

  const applyFilter = () => {
    onChange(dataKey, value);
    handleClose();
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleOpen = async () => {
    setOpen(true);
  };

  const visibleSelectedValues = useMemo(
    () => {
      let newValue = [...value];
      const parentItems = selectorOptions.filter(item => !!item.child);

      parentItems.forEach(item => {
        const isSelected = item.child.every(r => value.includes(r));
        if (isSelected) {
          newValue = newValue.filter(child => !item.child.includes(child));
          newValue = [item.value, ...newValue];
        }
      });
      return newValue;
    },
    [value],
  );

  const isStatus = dataKey.toLowerCase().includes('status') || dataKey === 'patientGender';
  const withBubbles =
    dataKey === 'accountCustomTags' ||
    dataKey === 'customTags' ||
    dataKey === 'patientCustomTags' ||
    dataKey === 'surveyTags';
  // dataKey === 'alertTags';

  return (
    <Select
      id={`select-${dataKey}`}
      ref={inputElement}
      value={visibleSelectedValues}
      open={open}
      onOpen={handleOpen}
      multiple={true}
      onChange={handleSelectChange}
      onClose={handleSelectClose}
      SelectProps={{
        classes: {
          select: classes.select,
          paper: classes.paper,
        },
        MenuProps: {
          getContentAnchorEl: null,
        },
      }}
      MenuProps={{
        classes: { paper: classes.paper },
        anchorOrigin: {
          vertical: 'bottom',
          horizontal: 'left',
        },
        transformOrigin: {
          vertical: 'top',
          horizontal: 'left',
        },
        anchorEl: containerElement ? containerElement.current : null,
        action: containerElement ? containerElement.current : null,
        getContentAnchorEl: null,
      }}

      input={
        <TextField
          id={`select-${dataKey}`}
          select
          value={filterValue}
          classes={{
            select: classes.select,
            root: classes.inputRoot,
            paper: classes.paper,
          }}
          InputProps={{
            classes: {
              input: classes.input,
              select: classes.select,
              paper: classes.paper,
            },
            disableUnderline: true,
            disabled: !isFilterable,
          }}
        >
          {(options || []).map((option) => (
            <MenuItem key={option.value} value={option.value}>
              <_OptionContent />
            </MenuItem>
          ))}
        </TextField>
      }
      renderValue={(selected) => getLabelByKey(selected)}
    >
      {selectorOptions.map((option) => {
        let isSelected = value.indexOf(option.value) > -1;

        if (!!option.child) {
          isSelected = option.child.every(r => value.includes(r));
        }

        return (
          <MenuItem key={option.value + (!!option.child ? 'parent' : '')}
                    value={!!option.child ? option.child.join('@@@') + 'parent_item' : option.value}
                    className={cx({
                      [classes.childItem]: option.isChild,
                      [classes.selected]: isSelected,
                      [classes.bottomDrawer]: option.value === '',
                      [classes.withBubble]: isStatus || withBubbles,
                    })}
          >
            {/*<div className="checkbox-wrapper-39">*/}
            {/*  <label>*/}
            {/*    <input type="checkbox" checked={isSelected}/>*/}
            {/*    <span className="checkbox"></span>*/}
            {/*  </label>*/}
            {/*</div>*/}
            <Checkbox checked={isSelected} />
            <_OptionContent option={option}
                            isStatus={isStatus}
                            withBubble={withBubble}
                            withBubbles={withBubbles} />
          </MenuItem>
        );
      })}
      {
        <MenuItem disableRipple
                  key={'button'}
                  value={'button'}
                  className={classes.buttonContainer}
        >
          <Button
            color='primary'
            disabled={false}
            variant={'contained'}
            className={cx(classes.button)}
            onClick={applyFilter}
          >
            {btn.applyFilter}
          </Button>
        </MenuItem>
      }
    </Select>
  );
};

SelectFilter.propTypes = {
  filterValue: PropTypes.arrayOf(PropTypes.string),
  dataKey: PropTypes.string.isRequired,
  columnData: PropTypes.shape({
    isFilterable: PropTypes.bool,
    options: PropTypes.array,
  }).isRequired,
  onChange: PropTypes.func.isRequired,
};

SelectFilter.defaultProps = {
  filterValue: [],
};

export default SelectFilter;

const _OptionContent = ({ option, isStatus, withBubble, withBubbles }) => {
  const classes = useStyles();

  return withBubbles && option.label !== 'All'
    ? <Badge label={option.label}
             colorType={getTagsColor(option.label)}
             customStyle={classes.badges} />
    : isStatus && option.label !== 'All'
      ? <Badge
        label={option.label}
        colorType={getStatusBadge(camelCase(option.label))}
        customStyle={classes.badges}
      />
      : withBubble && option.label !== 'All'
        ? <Badge
          label={option.label}
          colorType={getStatusBadge(option.label)}
          customStyle={classes.badges}
        />
        : <ListItemText primary={option.label}
                        className={cx({
                          [classes.childItemText]: option.isChild,
                          [classes.parentItemText]: !!option.child,
                        })} />;
};


